// lib/user-activities.ts
import type {
  PortalView,
  SessionTableFocusView,
  UserActivity,
} from '#types/types';

const USER_ACTIVITY_SET = new Set<UserActivity>(['DESIGN', 'SOURCE']);

const DEFAULT_USER_ACTIVITIES: readonly UserActivity[] = ['DESIGN'];
export const LEGACY_COMPAT_USER_ACTIVITIES: readonly UserActivity[] = ['DESIGN', 'SOURCE'];

const SOURCE_ONLY_PORTAL_VIEWS = new Set<PortalView | SessionTableFocusView>([
  'properties',
  'units',
  'marketSignals',
  'brands',
  'contacts',
  'portfolios',
  'deals',
  'audits',
  'legalEntities',
  'organisations',
  'globalActivity',
  'actionCenter',
  'data',
  'portfolioFits',
]);

const DESIGN_MPRACE_ENTITY_TYPES = [
  'Project',
  'Workspace',
  'Layer',
  'WorkspaceVersion',
  'GenerationTaskSnapshot',
] as const;

const SOURCE_MPRACE_ENTITY_TYPES = [
  'Portfolio',
  'Property',
  'Floor',
  'Unit',
  'Deal',
  'Brand',
  'Contact',
  // The unified campaign table, every granularity. It is not optional here: a Dealer turn is the
  // only MPRACE caller that passes an allowlist, so leaving it out makes every Campaign edge
  // unwalkable and the agent cannot answer "which campaign produced this lead" at all.
  'Campaign',
  'Lead',
  'PortfolioFit',
  'MarketSignal',
  'DataAudit',
  'LegalEntity',
  'DocumentTemplate',
  'GeneratedDocument',
  'UniversalFact',
] as const;

const ALWAYS_ALLOWED_MPRACE_ENTITY_TYPES = [
  'User',
  'Organisation',
  'ChatSession',
  'TimelineEvent',
] as const;

/**
 * WHAT A SOURCING TURN MAY NEVER DO, whatever it was asked and whoever asked it.
 *
 * A sourcing pass ends at the named verdict. On 2026-08-16 one did not: after scoring, the agent
 * created a draft deal, opened its page, decided the organisation had no Letter of Intent template,
 * tried to create one, failed on a mutation the database does not allow, and filed a SUPPORT TICKET
 * about its own failure. The user watched a deal page open that he had never asked for.
 *
 * These commands are not "discouraged" in a prompt, they are absent from the surface the agent is
 * handed, because a sentence in a prompt is a hope and this has now been reported three times.
 * Removing them here removes them from every agent turn in every mode; nothing in the interface is
 * touched, so the Drafting tab of a Deal page still works for a human who chooses to open it.
 */
export const POST_SOURCING_COMMAND_ACTIONS = new Set<string>([
  // Renders a PDF for a Deal from a DocumentTemplate. The Letter of Intent above, exactly.
  'GENERATE_DOCUMENT',
  // The agent escalated its OWN failed mutation to the support queue, unprompted. Whatever else a
  // support ticket is for, it is not for an agent filing bugs about itself while a user watches.
  'CREATE_SUPPORT_TICKET',
]);

/**
 * Records an agent may never CREATE, for the same reason and on the same day.
 *
 * Withholding the commands above is not enough on its own: `MUTATE` is generic, so the entity is a
 * parameter and "create a Deal" is reachable without any deal-specific command existing. The agent
 * did exactly that before it went looking for a document template.
 *
 * CREATE only. Updating a deal the user is working on stays available, because that is a person
 * asking for help with a record they already opened; conjuring the record is what nobody asked for.
 */
export const AGENT_UNCREATABLE_ENTITY_TYPES = new Set<string>([
  'Deal',
  'GeneratedDocument',
]);

export const SOURCE_ONLY_COMMAND_ACTIONS = new Set<string>([
  'DISCOVER_BRAND_CONTACTS',
  'LAUNCH_SOURCING_CAMPAIGN',
  'SOURCE_SHOW_IN_CHAT',
  // The two halves of a shortlist's doctrine are as Source-specific as the rubric command above.
  'READ_SHORTLIST_DOCTRINE',
  'UPDATE_SHORTLIST_DOCTRINE',
  // The free re-rank that makes a rubric edit visible. Source-specific for the same reason.
  'REAPPLY_RUBRIC',
  // The user's reusable scoring preset: the same two objects as above, saved off one shortlist and
  // written back onto another. Nothing outside Source has a shortlist to save from or apply to.
  'SAVE_SOURCING_PRESET',
  'APPLY_SOURCING_PRESET',
  // The shortlist as a sendable PDF. Same reason again: it reads one shortlist, and a user without
  // Source has none to export.
  'EXPORT_SHORTLIST',
]);

export function normalizeUserActivities(
  value: unknown,
  options?: { fallback?: readonly UserActivity[] }
): UserActivity[] {
  const fallback = options?.fallback ?? DEFAULT_USER_ACTIVITIES;
  const raw = Array.isArray(value) ? value : [];
  const normalized = raw
    .map((entry) => (typeof entry === 'string' ? entry.trim().toUpperCase() : ''))
    .filter((entry): entry is UserActivity => USER_ACTIVITY_SET.has(entry as UserActivity));

  const deduped = Array.from(new Set(normalized));
  if (deduped.length > 0) return deduped;
  return Array.from(new Set(fallback));
}

function hasUserActivity(
  activities: readonly UserActivity[] | null | undefined,
  activity: UserActivity
): boolean {
  return Array.isArray(activities) && activities.includes(activity);
}

export function isDesignOnlyActivities(
  activities: readonly UserActivity[] | null | undefined
): boolean {
  return hasUserActivity(activities, 'DESIGN') && !hasUserActivity(activities, 'SOURCE');
}

function isSourcePortalView(view: PortalView | SessionTableFocusView): boolean {
  return SOURCE_ONLY_PORTAL_VIEWS.has(view);
}

export function canAccessPortalView(
  activities: readonly UserActivity[] | null | undefined,
  view: PortalView | SessionTableFocusView
): boolean {
  if (!isSourcePortalView(view)) return true;
  return hasUserActivity(activities, 'SOURCE');
}

export function resolveAllowedMpraceEntityTypes(
  activities: readonly UserActivity[] | null | undefined
): string[] {
  const output = new Set<string>(ALWAYS_ALLOWED_MPRACE_ENTITY_TYPES);
  if (hasUserActivity(activities, 'DESIGN')) {
    DESIGN_MPRACE_ENTITY_TYPES.forEach((entityType) => output.add(entityType));
  }
  if (hasUserActivity(activities, 'SOURCE')) {
    SOURCE_MPRACE_ENTITY_TYPES.forEach((entityType) => output.add(entityType));
  }
  return Array.from(output);
}
